import * as React from 'react';
import CheckMarkIcon from '@narvar/nth-bit-icon-check-mark';
import { BigHeader, MediumHeader } from '@narvar/nth-bit-header';
import Paragraph from '@narvar/nth-bit-paragraph';
import TextLink, { TextLinkTargetType } from '@narvar/nth-bit-text-link';
import Button from '@narvar/nth-block-button';
import { Row } from '@narvar/nth-grid';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import {
  useTranslations,
  withTranslationsProvider,
} from '@narvar/nth-hook-translations';
import {
  UI_STATE,
  useRedirectToHoldContext,
} from '../../../hooks/useRedirectToHoldState';
import { DividerWrappedDiv, FirstCol, SecondCol, ThirdCol } from './components';
import LocationAddress from '../LocationAddress';
import { FEDEX_DELIVERY_MANAGER_LINK } from '../../../lib/links';
import ErrorAlert from '../../ErrorAlert';
import CustomComponentContext from '../../CustomComponentContext';

interface SuccessPanelProps {
  featureName: string;
}

const SuccessPanel: React.FunctionComponent<SuccessPanelProps> = ({
  featureName,
}: SuccessPanelProps) => {
  const { track } = React.useContext(CustomComponentContext);
  const { state: rthState, update: updateRthState } =
    useRedirectToHoldContext();

  const {
    redirectToHoldSuccessHeader,
    redirectToHoldPickupInstructionsHeader,
    redirectToHoldPickupInstructionsDescription,
    redirectToHoldSuccessDeliveryManagerHeading,
    redirectToHoldSuccessDeliveryManagerDescription,
    redirectToHoldSuccessDeliveryManagerLink,
    redirectToHoldErrorMissingLocation,
    redirectToHoldSelectLocation,
    redirectToHoldErrorMissingAddress,
    redirectToHoldVerify,
  } = useTranslations();

  const {
    bodytextDefaultNone,
    linksNoneDefault,
    linksNoneHover,
    dividerPrimaryNone,
  } = useTheme();

  const { address, selectedLocation: location } = rthState;

  // For this component to be valid, a selected location must exist
  // If not, then show an error UI to go back and choose a location
  if (location === undefined)
    return (
      <>
        <ErrorAlert
          show
          focusOnShow
          error
          dismissible={false}
          role="alert"
          timeout={0}
          s={{
            padding: '1rem',
            margin: '1rem 0',
            borderRadius: '.25rem',
          }}
        >
          <Paragraph containerCustomStyle={{ margin: 0, color: '#000' }}>
            {redirectToHoldErrorMissingLocation}
          </Paragraph>
        </ErrorAlert>
        <Button
          onClick={() =>
            updateRthState({ ...rthState, uiState: UI_STATE.SELECT_LOCATION })
          }
        >
          {redirectToHoldSelectLocation}
        </Button>
      </>
    );

  // For this component to be valid, a given address must exist
  // If not, then show an error UI to go back and verify an address
  if (address === undefined)
    return (
      <>
        <ErrorAlert
          show
          focusOnShow
          error
          dismissible={false}
          role="alert"
          timeout={0}
          s={{
            padding: '1rem',
            margin: '1rem 0',
            borderRadius: '.25rem',
          }}
        >
          <Paragraph containerCustomStyle={{ margin: 0, color: '#000' }}>
            {redirectToHoldErrorMissingAddress}
          </Paragraph>
        </ErrorAlert>
        <Button
          onClick={() =>
            updateRthState({ ...rthState, uiState: UI_STATE.VERIFY_ADDRESS })
          }
        >
          {redirectToHoldVerify}
        </Button>
      </>
    );

  const handleClickDeliveryManagerLink = () => {
    track({
      eventType: 'click',
      clickSubType: 'external_link',
    });
  };

  const addressLine1 = `${location.address.street1}${
    location.address.street2 ? ', ' : ''
  }${location.address.street2 || ''}`;
  const addressLine2 = `${location.address.city}, ${location.address.stateOrProvince} ${location.address.postalCode}`;

  return (
    <div style={{ paddingTop: '0.5rem' }}>
      <Row>
        <FirstCol xs-columns={12} sm-columns={4}>
          <CheckMarkIcon />
          <BigHeader as="h2" containerCustomStyle={{ marginTop: '1rem' }}>
            {redirectToHoldSuccessHeader}
          </BigHeader>
        </FirstCol>
        <SecondCol xs-columns={12} sm-columns={4}>
          <DividerWrappedDiv dividerStyles={dividerPrimaryNone}>
            <LocationAddress
              containerStyle={{ marginBottom: '1.5rem' }}
              name={location.name || ''}
              addressLine1={addressLine1}
              addressLine2={addressLine2}
              featureName={featureName}
              locationType={location.additionalDescription}
            />
            <div>
              <MediumHeader
                as="h3"
                containerCustomStyle={{ marginBottom: '0.5rem' }}
              >
                {redirectToHoldPickupInstructionsHeader}
              </MediumHeader>
              <div style={bodytextDefaultNone}>
                {redirectToHoldPickupInstructionsDescription}
              </div>
            </div>
          </DividerWrappedDiv>
        </SecondCol>
        <ThirdCol xs-columns={12} sm-columns={4}>
          <MediumHeader
            as="h3"
            containerCustomStyle={{ marginBottom: '0.5rem' }}
          >
            {redirectToHoldSuccessDeliveryManagerHeading}
          </MediumHeader>
          <div style={{ ...bodytextDefaultNone, marginBottom: '1rem' }}>
            {redirectToHoldSuccessDeliveryManagerDescription}
          </div>
          <TextLink
            linkStyle={{
              sDefault: linksNoneDefault,
              sHover: linksNoneHover,
            }}
            href={FEDEX_DELIVERY_MANAGER_LINK}
            target={TextLinkTargetType.BLANK}
            onClick={handleClickDeliveryManagerLink}
          >
            {redirectToHoldSuccessDeliveryManagerLink}
          </TextLink>
        </ThirdCol>
      </Row>
    </div>
  );
};

export default withTranslationsProvider(
  withThemeProvider(SuccessPanel, {
    themeTokens: [
      'bodytextDefaultNone',
      'captiontextDefaultNone',
      'linksNoneDefault',
      'linksNoneHover',
      'dividerPrimaryNone',
    ],
  }),
  {
    translationKeys: [
      'redirectToHoldSuccessHeader',
      'redirectToHoldPickupInstructionsHeader',
      'redirectToHoldPickupInstructionsDescription',
      'redirectToHoldSuccessDeliveryManagerHeading',
      'redirectToHoldSuccessDeliveryManagerDescription',
      'redirectToHoldSuccessDeliveryManagerLink',
      'redirectToHoldErrorMissingLocation',
      'redirectToHoldSelectLocation',
      'redirectToHoldErrorMissingAddress',
      'redirectToHoldVerify',
    ],
  },
);
